<script setup>
import { ref, onBeforeMount, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useLayout } from '@/layout/composables/layout';
import { useFavorites } from './composables/useFavorites';

const emit = defineEmits(['update:favorited']); // Define the emit function for the favorited event

const route = useRoute();
const store = useStore();

const { 
    layoutConfig, 
    layoutState, 
    setActiveMenuItem, 
    onMenuToggle,
} = useLayout();

const { 
    favorites,
    addFavorite,
    removeFavorite
} = useFavorites(store.state.auth.user);

const computedFavorites = computed(() => store.getters['auth/favorites']);

// Normalize the 'to' and 'url' properties for comparison
const normalizeUrl = (item) => {
    return item.to || item.url;
};

const props = defineProps({
    item: {
        type: Object,
        default: () => ({})
    },
    index: {
        type: Number,
        default: 0 
    },
    root: {
        type: Boolean,
        default: true
    },
    parentItemKey: {
        type: String,
        default: null
    },
    isFavorite: {
        type: Boolean,
        default: false
    }
});

const isActiveMenu = ref(false);
const itemKey = ref(null);

// Use a computed property for isFavorite, so it reacts to Vuex store changes
const isFavorite = computed(() => {
  const itemUrl = normalizeUrl(props.item);
  return computedFavorites.value.some(fav => normalizeUrl(fav) === itemUrl);
});

onBeforeMount(() => {
    itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);
});

watch(
    () => layoutConfig.activeMenuItem.value,
    (newVal) => {
        isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
    }
);

const itemClick = (event, item) => {
    if (item.disabled) {
        event.preventDefault();
        return;
    }

    const { overlayMenuActive, staticMenuMobileActive } = layoutState;

    if ((item.to || item.url) && staticMenuMobileActive.value) {
        onMenuToggle();
    }

    if (item.command) {
        item.command({ originalEvent: event, item: item });
    }

    const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey) : itemKey.value;
    setActiveMenuItem(foundItemKey);
};

const checkActiveRoute = (item) => {
    return route.path === item.to;
};

const isInternalUser = computed(() => {
    return store.getters['auth/isInternalUser'];
});
const isOnVPN = computed(() => {
    return store.getters['auth/isOnVpn'];
});
const userCan = (ability) => {
    return store.getters['auth/userCan'](ability);
};
const isInternalRoute = (item) => {
    return item.to && item.to.includes('/internal');
};

const toggleFavorite = async (event, item) => {
  console.log('Toggling favorite for item:', item);
  event.stopPropagation();
  event.preventDefault();

  const itemUrl = normalizeUrl(item);

  if (isFavorite.value) {
    // If it’s already a favorite, remove it
    await removeFavorite(itemUrl);
  } else {
    // If it’s not a favorite, add it
    await addFavorite({ ...item, url: itemUrl });
  }

  // Emit the updated favorite status to the parent component after async operation
  emit('update:favorited', { item, isFavorite: !isFavorite.value });
};

</script>

<template>
    <li
        :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }"
        v-if="(!item.requiresInternal || isInternalUser) && (!item.requiresAbility || userCan(item.requiresAbility))"
        v-tooltip="!isOnVPN && isInternalRoute(item) && 'You must be on the VPN to access this page!'"
    >
        <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">{{ item.label }}</div>

        <a v-if="(!item.to || item.items) && item.visible !== false" :href="item.url" @click="itemClick($event, item, index)" :class="item.class" :target="item.target" tabindex="0">
            <button v-if="item.to || item.items" @click="toggleFavorite($event, item)" class="favorite-button">
                <i :class="isFavorite ? 'pi pi-star-fill' : 'pi pi-star'"></i>
            </button>
            
            <i :class="item.icon" class="layout-menuitem-icon"></i>
            <span class="layout-menuitem-text">{{ item.label }}</span>
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
        </a>

        <router-link
            v-if="item.to && !item.items && item.visible !== false"
            @click="itemClick($event, item, index)"
            :target="item.target"
            :class="[item.class, { 'active-route': checkActiveRoute(item), 'p-disabled': !isOnVPN && isInternalRoute(item) }]"
            tabindex="0"
            :to="item.to"
        >
            <button v-if="item.to || item.items" @click="toggleFavorite($event, item)" class="favorite-button">
                <i :class="isFavorite ? 'pi pi-star-fill' : 'pi pi-star'"></i>
            </button>

            <i :class="!isOnVPN && isInternalRoute(item) ? 'pi pi-exclamation-circle' : item.icon" class="layout-menuitem-icon"></i>
            <span class="layout-menuitem-text">{{ item.label }}</span>
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
        </router-link>

        <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
            <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
                <app-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parentItemKey="itemKey" :root="false"></app-menu-item>
            </ul>
        </Transition>
    </li>
</template>

<style lang="scss" scoped>
.layout-menuitem {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
}

.favorite-button {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
}

.favorite-button i {
    font-size: 0.85rem;
    color: #9e9e9e;
    transition: color 0.3s ease;
}

.favorite-button i:hover {
    color: #757575;
}

.layout-menuitem-icon {
    font-size: 1.35em;
    margin-right: 10px;
}

.layout-menuitem-text {
    flex-grow: 1;
}

.layout-menu-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
}
</style>
